import {
  EmploymentStatus,
  PracticePreferencePrevisitAddressValidCharacterSet,
  Race,
  Relationship,
  SexUpper,
} from 'primary-schema';

import { DefaultSexes, ModificationStatus, PhoneNumberKind } from './common.js';
import type * as Encounter from './encounter.js';
import {
  type ConditionWithRelationship,
  type FamilyMedicalHistory,
  FamilyRelationship,
  type MedicalCondition,
} from './familyMedicalHistory.js';
import {
  AlcoholUsageStatus,
  Contraceptive,
  SexAtBirth,
  SexualIdentity,
  SexualOrientation,
  SexualPronoun,
  TobaccoAmount,
  TobaccoStatus,
} from './patient/socialHistory.js';
import type * as Patient from './patient.js';
import { Pronoun } from './patient.js';
import { Frequency, type Preferences } from './practice/preference.js';
import type * as Practice from './practice.js';
import type * as PrevisitFormSet from './previsit/formSet.js';
import { Primitive } from './previsit/formTemplateFieldConfig.js';
import type * as User from './user.js';

export type { ConditionWithRelationship, FamilyMedicalHistory, MedicalCondition };

export { FamilyRelationship, Frequency, SexUpper, DefaultSexes, ModificationStatus };

export enum AuthErrorType {
  AuthCode = 'AUTH_CODE',
  ClosedFormSet = 'CLOSED_FORM_SET',
  EmailNotMatched = 'EMAIL_NOT_MATCHED',
  EmptyFormSet = 'EMPTY_FORM_SET',
  EncounterCancelled = 'ENCOUNTER_CANCELLED',
  EncounterDeleted = 'ENCOUNTER_DELETED',
  Expired = 'EXPIRED',
  FormSetNotFound = 'FORM_SET_NOT_FOUND',
  MaxAttempts = 'MAX_ATTEMPTS',
  PaymentNotFound = 'PAYMENT_NOT_FOUND',
  PhoneNotMatched = 'PHONE_NOT_MATCHED',
  RequiredCode = 'REQUIRED_CODE',
  RequiredZip = 'REQUIRED_ZIP',
  Unauthenticated = 'UNAUTHENTICATED',
}

export const addressValidCharacterSetMap: {
  [characterSet in PracticePreferencePrevisitAddressValidCharacterSet]: string;
} = {
  [PracticePreferencePrevisitAddressValidCharacterSet.AlphanumericAndSlash]: '[^0-9a-zA-Z\\/ ]',
  [PracticePreferencePrevisitAddressValidCharacterSet.Any]: '',
};

export const relationshipOptionsMap: {
  [value in Relationship]: string;
} = {
  [Relationship.AdoptedChild]: 'Adopted child',
  [Relationship.Aunt]: 'Aunt',
  [Relationship.AuntUncle]: 'Aunt or uncle',
  [Relationship.Brother]: 'Brother',
  [Relationship.CadaverDonor]: 'Cadaver donor',
  [Relationship.Child]: 'Child',
  [Relationship.ChildInLaw]: 'Child in law',
  [Relationship.ChildNoFinancial]: 'Child where insured has no financial responsibility',
  [Relationship.CollateralDependent]: 'Collateral dependent',
  [Relationship.CourtAppointedGuardian]: 'Court appointed guardian',
  [Relationship.Cousin]: 'Cousin',
  [Relationship.Daughter]: 'Daughter',
  [Relationship.DepChild]: 'Dependent child',
  [Relationship.DependentOfMinorDependent]: 'Dependent of minor dependent',
  [Relationship.DomesticPartnership]: 'Domestic partnership',
  [Relationship.Donor]: 'Donor',
  [Relationship.EmancipatedMinor]: 'Emancipated minor',
  [Relationship.Employee]: 'Employee',
  [Relationship.Employer]: 'Employer',
  [Relationship.ExSpouse]: 'Ex spouse',
  [Relationship.Father]: 'Father',
  [Relationship.FatherInLaw]: 'Father in law',
  [Relationship.FosterChild]: 'Foster child',
  [Relationship.FosterParent]: 'Foster parent',
  [Relationship.Friend]: 'Friend',
  [Relationship.Grandchild]: 'Grandchild',
  [Relationship.Husband]: 'Husband',
  [Relationship.Granddaughter]: 'Granddaughter',
  [Relationship.Grandfather]: 'Grandfather',
  [Relationship.Grandmother]: 'Grandmother',
  [Relationship.Grandparent]: 'Grandparent',
  [Relationship.Grandson]: 'Grandson',
  [Relationship.GreatGrandfather]: 'Great grandfather',
  [Relationship.GreatGrandmother]: 'Great grandmother',
  [Relationship.Guardian]: 'Guardian',
  [Relationship.HandicappedDependent]: 'Handicapped dependent',
  [Relationship.InjuredPlaintiff]: 'Injured plaintiff',
  [Relationship.InLaw]: 'In law',
  [Relationship.Legal]: 'Legal guardian',
  [Relationship.LifePartner]: 'Life partner',
  [Relationship.Mother]: 'Mother',
  [Relationship.MotherInLaw]: 'Mother in law',
  [Relationship.Neighbor]: 'Neighbor',
  [Relationship.NieceNephew]: 'Niece or nephew',
  [Relationship.OrganDonor]: 'Organ donor',
  [Relationship.Other]: 'Other',
  [Relationship.OtherAdult]: 'Other adult',
  [Relationship.OverageDependent]: 'Overage dependent',
  [Relationship.Parent]: 'Parent',
  [Relationship.ParentInLaw]: 'Parent in law',
  [Relationship.Recipient]: 'Recipient',
  [Relationship.Relative]: 'Relative',
  [Relationship.Roommate]: 'Roommate',
  [Relationship.Self]: 'Self',
  [Relationship.Sibling]: 'Sibling',
  [Relationship.SiblingInLaw]: 'Sibling in law',
  [Relationship.SignificantOther]: 'Significant other',
  [Relationship.Sister]: 'Sister',
  [Relationship.Son]: 'Son',
  [Relationship.Sponsor]: 'Sponsor',
  [Relationship.SponsoredDependent]: 'Sponsored dependent',
  [Relationship.Spouse]: 'Spouse',
  [Relationship.Stepchild]: 'Stepchild',
  [Relationship.Stepparent]: 'Stepparent',
  [Relationship.Stepfather]: 'Stepfather',
  [Relationship.Stepmother]: 'Stepmother',
  [Relationship.Student]: 'Student',
  [Relationship.Uncle]: 'Uncle',
  [Relationship.Unknown]: 'Unknown',
  [Relationship.Unverified]: 'Unverified proxy',
  [Relationship.Ward]: 'Ward',
  [Relationship.Wife]: 'Wife',
};

export const phoneNumberKindMap: {
  [kind in PhoneNumberKind]: string;
} = {
  [PhoneNumberKind.Cell]: 'Cell',
  [PhoneNumberKind.Home]: 'Home',
  [PhoneNumberKind.Work]: 'Work',
};

export const tobaccoStatusMap: { [status in TobaccoStatus]: string } = {
  [TobaccoStatus.Never]: 'Never smoker',
  [TobaccoStatus.Former]: 'Formerly smoker',
  [TobaccoStatus.Somedays]: 'Current someday smoker',
  [TobaccoStatus.Everyday]: 'Current everyday smoker',
  [TobaccoStatus.Light]: 'Light tobacco smoker',
  [TobaccoStatus.Heavy]: 'Heavy tobacco smoker',
  [TobaccoStatus.Unknown]: 'Refuse to disclose',
  [TobaccoStatus.Other]: 'Other',
};

export const tobaccoAmountMap: { [amount in TobaccoAmount]: string } = {
  [TobaccoAmount.QuarterPack]: '1/4 pack',
  [TobaccoAmount.HalfPack]: '1/2 pack',
  [TobaccoAmount.OnePack]: '1 pack',
  [TobaccoAmount.TwoPacks]: '2 packs',
  [TobaccoAmount.ThreePacks]: '3 packs',
  [TobaccoAmount.MoreThanThreePacks]: '> 3 packs',
  [TobaccoAmount.Unknown]: 'Refuse to disclose',
  [TobaccoAmount.Other]: 'Other',
};

export const alcoholUsageStatusMap: {
  [value in AlcoholUsageStatus]: string;
} = {
  [AlcoholUsageStatus.CurrentlyUse]: 'Currently use',
  [AlcoholUsageStatus.NeverUsed]: 'Never used',
  [AlcoholUsageStatus.FormerlyUsed]: 'Formerly used',
  [AlcoholUsageStatus.NotSincePregnant]: 'Not used since pregnant',
  [AlcoholUsageStatus.InEarlyPregnancy]: 'Used early in pregnancy',
  [AlcoholUsageStatus.InRecovery]: 'In recovery',
  [AlcoholUsageStatus.PreferNotToAnswer]: 'Prefer not to answer',
  [AlcoholUsageStatus.UnableToObtain]: 'Unable to obtain',
  [AlcoholUsageStatus.Other]: 'Other',
};

export const FrequencyMap: {
  [value in Frequency]: string;
} = {
  [Frequency.DailySeveralTimes]: 'Several times per day',
  [Frequency.Daily]: 'Daily',
  [Frequency.Weekly]: '1-2 times per week',
  [Frequency.WeeklyMoreThanThree]: '3-5 times per week',
  [Frequency.Monthly]: '1-2 times per month',
  [Frequency.Yearly]: '1-2 times per year',
  [Frequency.Other]: 'Other',
  [Frequency.OncePerWeek]: 'Once per week',
  [Frequency.TwoTimesPerWeek]: 'Two times per week',
  [Frequency.ThreeTimesPerWeek]: 'Three times per week',
  [Frequency.FourTimesPerWeek]: 'Four times per week',
  [Frequency.FiveTimesPerWeek]: 'Five times per week',
  [Frequency.SixTimesPerWeek]: 'Six times per week',
  [Frequency.EveryDayOfTheWeek]: 'Every day of the week',
};

export const sexualPronounMap: Record<SexualPronoun, string> = {
  [SexualPronoun.SheHer]: 'She / her',
  [SexualPronoun.HeHim]: 'He / him',
  [SexualPronoun.TheyTheir]: 'They / their',
  [SexualPronoun.Other]: 'Something else',
};

export const sexualIdentityMap: Record<SexualIdentity, string> = {
  [SexualIdentity.Female]: 'Female',
  [SexualIdentity.Male]: 'Male',
  [SexualIdentity.MaleToFemaleTransWoman]: 'Transgender (Male to female)',
  [SexualIdentity.FemaleToMaleTransMan]: 'Transgender (Female to male)',
  [SexualIdentity.Queer]: 'Queer',
  [SexualIdentity.Genderqueer]: 'Genderqueer',
  [SexualIdentity.Nonbinary]: 'Non-Binary',
  [SexualIdentity.NonDisclosure]: 'Prefer not to answer',
  [SexualIdentity.Other]: 'Something else',
  [SexualIdentity.OtherNoCustomInput]: 'Something else',
};

export const sexAtBirthMap: Record<SexAtBirth, string> = {
  [SexAtBirth.Female]: 'Female',
  [SexAtBirth.Male]: 'Male',
  [SexAtBirth.Intersex]: 'Inter-sex',
  [SexAtBirth.NonDisclosure]: 'Prefer not to answer',
  [SexAtBirth.Other]: 'Something else',
};

export const contraceptiveMap: Record<Contraceptive, string> = {
  [Contraceptive.Abstinence]: 'Abstinence',
  [Contraceptive.BirthControlPill]: 'Birth control pills',
  [Contraceptive.Condom]: 'Condom',
  [Contraceptive.FemaleSterilization]: 'Female Sterilization',
  [Contraceptive.HormoneShot]: 'Hormone shot',
  [Contraceptive.Hysterectomy]: 'Hysterectomy',
  [Contraceptive.Iud]: 'IUD',
  [Contraceptive.MaleSterilization]: 'Vasectomy',
  [Contraceptive.NaturalPlanning]: 'Natural planning',
  [Contraceptive.NoContraceptive]: 'None',
  [Contraceptive.Other]: 'Other',
  [Contraceptive.Patch]: 'Patch',
  [Contraceptive.PlanB]: 'Plant B',
  [Contraceptive.PostMenopausal]: 'Post Menopausal',
  [Contraceptive.PullOutMethod]: 'Pull out method',
  [Contraceptive.Ring]: 'Vaginal Ring',
  [Contraceptive.VaginalInsert]: 'Vaginal insert',
};

export const sexualOrientationMap: Record<SexualOrientation, string> = {
  [SexualOrientation.Homosexual]: 'Lesbian, gay or homosexual',
  [SexualOrientation.Heterosexual]: 'Straight or heterosexual',
  [SexualOrientation.Bisexual]: 'Bisexual',
  [SexualOrientation.DontKnow]: 'Dont know',
  [SexualOrientation.ChooseNotToDisclose]: 'Choose not to disclose',
  [SexualOrientation.Other]: 'Other',
};

export const employmentStatusOptionsMap: {
  [value in EmploymentStatus]: string;
} = {
  [EmploymentStatus.Disabled]: 'Disabled',
  [EmploymentStatus.Employed]: 'Employed',
  [EmploymentStatus.NotEmployed]: 'Not employed',
  [EmploymentStatus.FullTime]: 'Full time',
  [EmploymentStatus.OnActiveMilitaryDuty]: 'On active military duty',
  [EmploymentStatus.Other]: 'Other',
  [EmploymentStatus.PartTime]: 'Part time',
  [EmploymentStatus.Retired]: 'Retired',
  [EmploymentStatus.RetiredMilitary]: 'Retired military',
  [EmploymentStatus.SelfEmployed]: 'Self employed',
  [EmploymentStatus.StudentFullTime]: 'Student full time',
  [EmploymentStatus.StudentPartTime]: 'Student part time',
  [EmploymentStatus.Unknown]: 'Unknown',
  [EmploymentStatus.Minor]: 'Minor',
};

export const DefaultEmploymentStatus = [
  EmploymentStatus.Disabled,
  EmploymentStatus.Employed,
  EmploymentStatus.NotEmployed,
  EmploymentStatus.FullTime,
  EmploymentStatus.OnActiveMilitaryDuty,
  EmploymentStatus.Other,
  EmploymentStatus.PartTime,
  EmploymentStatus.Retired,
  EmploymentStatus.RetiredMilitary,
  EmploymentStatus.SelfEmployed,
  EmploymentStatus.StudentFullTime,
  EmploymentStatus.StudentPartTime,
  EmploymentStatus.Unknown,
];

export interface EmergencyContact {
  emergencyContactFirstName: string | null;
  emergencyContactLastName: string | null;
  emergencyContactPhone: string | null;
  emergencyContactRelationship: keyof typeof relationshipMap | null;
  patientRelationshipToEmergencyContact: keyof typeof relationshipMap | null;
}

export interface PatientEmergencyContact {
  firstName: string;
  lastName: string;
  rank: number;
  cellPhoneNumber: string | null;
  homePhoneNumber: string | null;
  emergencyContactRelationshipToPatient: keyof typeof relationshipMap;
  patientRelationshipToEmergencyContact: keyof typeof relationshipMap | null;
  status: ModificationStatus;
}

export enum Ethnicity {
  Chicano = 'CHICANO',
  Cuban = 'CUBAN',
  Declined = 'PATIENT_DECLINED',
  Hispanic = 'HISPANIC',
  Mexican = 'MEXICAN',
  NonHispanic = 'NON-HISPANIC',
  PuertoRican = 'PUERTO_RICAN',
  Unknown = 'UNKNOWN',
}
export const ethnicityOptionsMap: { [value in Ethnicity]: string } = {
  [Ethnicity.Chicano]: 'Chicano',
  [Ethnicity.Cuban]: 'Cuban',
  [Ethnicity.Declined]: 'Do not wish to provide',
  [Ethnicity.Hispanic]: 'Hispanic, Latino or Spanish Origin',
  [Ethnicity.Mexican]: 'Mexican',
  [Ethnicity.PuertoRican]: 'Puerto Rican',
  [Ethnicity.NonHispanic]: 'Not Hispanic, Latino or Spanish Origin',
  [Ethnicity.Unknown]: 'Unknown',
};

export enum MaritalStatus {
  CivilUnion = 'CIVIL_UNION',
  Divorced = 'DIVORCED',
  LifePartner = 'LIFE_PARTNER',
  Married = 'MARRIED',
  Partnered = 'PARTNERED',
  Separated = 'SEPARATED',
  Single = 'SINGLE',
  Widowed = 'WIDOWED',
  Other = 'OTHER',
}

export const DefaultMaritalStatus = [
  MaritalStatus.Divorced,
  MaritalStatus.Married,
  MaritalStatus.Partnered,
  MaritalStatus.Separated,
  MaritalStatus.Single,
  MaritalStatus.Widowed,
];

export const maritalStatusOptionsMap: { [value in MaritalStatus]: string } = {
  [MaritalStatus.CivilUnion]: 'Civil Union',
  [MaritalStatus.Divorced]: 'Divorced',
  [MaritalStatus.LifePartner]: 'Life Partner',
  [MaritalStatus.Married]: 'Married',
  [MaritalStatus.Partnered]: 'Partnered',
  [MaritalStatus.Separated]: 'Separated',
  [MaritalStatus.Single]: 'Single',
  [MaritalStatus.Widowed]: 'Widowed',
  [MaritalStatus.Other]: 'Other',
};

export const sexOptionsMap: { [value in SexUpper]: string } = {
  [SexUpper.Female]: 'Female',
  [SexUpper.Male]: 'Male',
  [SexUpper.NonBinary]: 'Non-binary/Gender non-conforming',
  [SexUpper.Other]: 'Other',
  [SexUpper.Transgender]: 'Transgender',
  [SexUpper.TransgenderFemale]: 'Transgender female',
  [SexUpper.TransgenderMale]: 'Transgender male',
  [SexUpper.Unknown]: 'Unknown',
};

export enum AssignedSexAtBirth {
  Female = 'FEMALE',
  Male = 'MALE',
  Unknown = 'UNKNOWN',
}

export const assignedSexAtBirthToContentOptionsMap: Record<AssignedSexAtBirth, string> = {
  [AssignedSexAtBirth.Female]: 'Female',
  [AssignedSexAtBirth.Male]: 'Male',
  [AssignedSexAtBirth.Unknown]: 'Unknown',
};

export type PrimitivesWithOptions =
  | Primitive.PatientAssignedSexAtBirth
  | Primitive.PatientEmploymentStatus
  | Primitive.PatientEthnicity
  | Primitive.PatientMaritalStatus
  | Primitive.PatientSex;

export type PrimitiveOptionTypes =
  | typeof assignedSexAtBirthToContentOptionsMap
  | typeof employmentStatusOptionsMap
  | typeof ethnicityOptionsMap
  | typeof maritalStatusOptionsMap
  | typeof sexOptionsMap;

export const getPrimitiveOptions = (primitive: PrimitivesWithOptions): PrimitiveOptionTypes => {
  switch (primitive) {
    case Primitive.PatientAssignedSexAtBirth:
      return assignedSexAtBirthToContentOptionsMap;
    case Primitive.PatientEmploymentStatus:
      return employmentStatusOptionsMap;
    case Primitive.PatientEthnicity:
      return ethnicityOptionsMap;
    case Primitive.PatientMaritalStatus:
      return maritalStatusOptionsMap;
    case Primitive.PatientSex:
      return sexOptionsMap;
  }
};

export const familyRelationshipsMap: { [key in FamilyRelationship]: string } = {
  [FamilyRelationship.AuntOrUncle]: 'Aunt/Uncle',
  [FamilyRelationship.Brother]: 'Brother',
  [FamilyRelationship.Child]: 'Child',
  [FamilyRelationship.Cousin]: 'Cousin',
  [FamilyRelationship.Daughter]: 'Daughter',
  [FamilyRelationship.Father]: 'Father',
  [FamilyRelationship.Grandchild]: 'Grandchild',
  [FamilyRelationship.Grandparent]: 'Grandparent',
  [FamilyRelationship.GreatGrandfather]: 'Great Grandfather',
  [FamilyRelationship.GreatGrandmother]: 'Great Grandmother',
  [FamilyRelationship.Mother]: 'Mother',
  [FamilyRelationship.MaternalAunt]: 'Maternal Aunt',
  [FamilyRelationship.MaternalGrandfather]: 'Maternal Grandfather',
  [FamilyRelationship.MaternalGrandmother]: 'Maternal Grandmother',
  [FamilyRelationship.MaternalUncle]: 'Maternal Uncle',
  [FamilyRelationship.NieceOrNephew]: 'Niece/Nephew',
  [FamilyRelationship.Other]: 'Other',
  [FamilyRelationship.PaternalAunt]: 'Paternal Aunt',
  [FamilyRelationship.PaternalGrandfather]: 'Paternal Grandfather',
  [FamilyRelationship.PaternalGrandmother]: 'Paternal Grandmother',
  [FamilyRelationship.PaternalUncle]: 'Paternal Uncle',
  [FamilyRelationship.Sibling]: 'Sibling',
  [FamilyRelationship.Sister]: 'Sister',
  [FamilyRelationship.Son]: 'Son',
  [FamilyRelationship.Spouse]: 'Spouse',
};

export const pronounMap: { [value in Pronoun]: string } = {
  [Pronoun.HeHimHis]: 'He/Him/His',
  [Pronoun.NoPronouns]: 'No Pronouns',
  [Pronoun.Other]: 'Other',
  [Pronoun.PleaseAsk]: 'Please Ask',
  [Pronoun.SheHerHers]: 'She/Her/Hers',
  [Pronoun.TheyThemTheirs]: 'They/Them/Theirs',
};

export const raceMap: { [value in Race]: string } = {
  [Race.African]: 'Black or African American',
  [Race.AfricanAmerican]: 'Black or African American',
  [Race.AmericanIndian]: 'American Indian or Alaska Native',
  [Race.Asian]: 'Asian',
  [Race.AsianIndian]: 'Asian Indian',
  [Race.Caucasian]: 'White or Caucasian',
  [Race.Chinese]: 'Chinese',
  [Race.Filipino]: 'Filipino',
  [Race.Guamanian]: 'Guamanian or Chamorro',
  [Race.Japanese]: 'Japanese',
  [Race.Korean]: 'Korean',
  [Race.MiddleEasternOrNorthAfrican]: 'Middle Eastern or North African',
  [Race.Mixed]: 'Mixed race',
  [Race.Other]: 'Other',
  [Race.PacificIslander]: 'Native Hawaiian or Pacific Islander',
  [Race.NativeHawaiian]: 'Native Hawaiian',
  [Race.PatientDeclined]: 'Do not wish to provide',
  [Race.Samoan]: 'Samoan',
  [Race.Vietnamese]: 'Vietnamese',
  [Race.White]: 'White',
  [Race.Unavailable]: 'Unavailable',
};

export const religionMap = {
  AMISH: 'Amish',
  AGNOSTIC: 'Agnostic',
  BUDDHIST: 'Buddhist',
  CATHOLIC: 'Catholic',
  CHRISTIAN: 'Christian',
  CHRISTIAN_SCIENTISTS: 'Christian Scientists',
  HINDU: 'Hindu',
  JEHOVAS_WITNESS: "Jehovah's Witness",
  JEWISH: 'Jewish',
  MORMON: 'Mormon',
  MUSLIM: 'Muslim',
  ORTHODOX_JEWS: 'Orthodox Jews',
  OTHER: 'Other',
  PREFER_NOT_TO_ANSWER: 'Prefer not to answer',
  SEVENTH_DAY_ADVENTISTS: 'Seventh Day Adventists',
  SIKHS: 'Sikhs',
  NONE: 'None',
};

export const relationshipMap = {
  AUNT: 'Aunt',
  AUNT_UNCLE: 'Aunt/Uncle',
  BROTHER: 'Brother',
  CADAVER_DONOR: 'Cadaver donor',
  CARE_GIVER: 'Caregiver',
  CHILD: 'Child',
  CHILD_IN_LAW: 'Child-in-law',
  CHILD_NO_FINANCIAL: 'Child - no financial responsibility',
  COUSIN: 'Cousin',
  DAUGHTER: 'Daughter',
  DEP_CHILD: 'Dependent child',
  DOMESTIC_PARTNERSHIP: 'Domestic partnership',
  DONOR: 'Donor',
  EMPLOYEE: 'Employee',
  EMPLOYER: 'Employer',
  EXTENDED_FAMILY: 'Extended family',
  EX_SPOUSE: 'Ex-spouse',
  FATHER: 'Father',
  FATHER_IN_LAW: 'Father-in-law',
  FOSTER_CHILD: 'Foster child',
  FOSTER_PARENT: 'Foster parent',
  FRIEND: 'Friend',
  FRIEND_NEIGHBOR: 'Friend/Neighbor',
  GRANDCHILD: 'Grandchild',
  GRANDDAUGHTER: 'Granddaughter',
  GRANDFATHER: 'Grandfather',
  GRANDMOTHER: 'Grandmother',
  GRANDPARENT: 'Grandparent',
  GRANDSON: 'Grandson',
  GREAT_GRANDFATHER: 'Great-grandfather',
  GREAT_GRANDMOTHER: 'Great-grandmother',
  GUARDIAN: 'Guardian',
  HUSBAND: 'Husband',
  IN_LAW: 'In-Law',
  LIFE_PARTNER: 'Life partner',
  MOTHER: 'Mother',
  MOTHER_IN_LAW: 'Mother-in-law',
  NEIGHBOR: 'Neighbor',
  NIECE_NEPHEW: 'Niece/Nephew',
  ORGAN_DONOR: 'Organ donor',
  OTHER: 'Other',
  OVERAGE_DEPENDENT: 'Overage dependent',
  PARENT: 'Parent',
  PARENT_IN_LAW: 'Parent-in-law',
  RECIPIENT: 'Recipient',
  RELATIVE: 'Relative',
  ROOMMATE: 'Roommate',
  SELF: 'Self',
  SIBLING: 'Sibling',
  SIBLING_IN_LAW: 'Sibling-in-law',
  SIGNIFICANT_OTHER: 'Significant other',
  SISTER: 'Sister',
  SON: 'Son',
  SPONSOR: 'Sponsor',
  SPONSORED_DEPENDENT: 'Sponsored dependent',
  SPOUSE: 'Spouse',
  STEPCHILD: 'Stepchild',
  STEPFATHER: 'Stepfather',
  STEPMOTHER: 'Stepmother',
  STEPPARENT: 'Stepparent',
  STUDENT: 'Student',
  UNCLE: 'Uncle',
  WARD: 'Ward',
  WIFE: 'Wife',
};

interface MedicalConditionListItem {
  name: string;
  defaultList: boolean; // does it show under the default list or under other search
}

export interface PracticePreference extends Pick<
  Preferences,
  | 'alcoholStatuses'
  | 'allergyExplicitNoneEnabled'
  | 'allergyReactions'
  | 'allergyNames'
  | 'allergySeverityOptions'
  | 'allowSelfForGuardianRelationship'
  | 'collectEmployerPhoneNumber'
  | 'contraceptiveOptions'
  | 'copayPrepayDisplaySetting'
  | 'digitalArrivalNotificationSendTimeOffset'
  | 'emergencyContactRelationships'
  | 'employers'
  | 'employmentStatuses'
  | 'enableDocumentSignerV2'
  | 'enableOutreachIntakes'
  | 'enablePaymentReceipts'
  | 'enablePrevisitPassbookAndPayPass'
  | 'enablePrevisitSubscriberToGuarantorRelationshipCollection'
  | 'enableProviderDirectory'
  | 'exportInsuranceWithNullCoverageStartDateEnabled'
  | 'familyMedicalHistoryRelationships'
  | 'frontDeskCheckInEnabled'
  | 'hideEncounterStartTimeByVisitKind'
  | 'imageQualityBlurThreshold'
  | 'patientAlcoholUsageFrequencies'
  | 'patientAlcoholUsageQuantities'
  | 'patientAppRteEnabled'
  | 'patientEnvironmentLivesWithOptions'
  | 'patientEnvironmentLivingSituationOptions'
  | 'patientEnvironmentParentsMaritalStatusOptions'
  | 'patientEnvironmentRisksOptions'
  | 'patientGuarantorDetailsEnabled'
  | 'patientGuarantorFieldsToExclude'
  | 'patientInsuranceInformationPayors'
  | 'patientRelationshipsToGuarantor'
  | 'patientRelationshipsToSubscriber'
  | 'patientSubstanceUsageFrequencies'
  | 'practiceCardOptions'
  | 'previousBalanceCollectionSetting'
  | 'previsitAddressValidCharacterSet'
  | 'previsitConfirmCancelFormSetRedirectEnabled'
  | 'previsitConfirmCancelMinimumCancelTime'
  | 'previsitDefaultCheckRequiredDocuments'
  | 'previsitEditGuarantorEnabled'
  | 'previsitEditInsuranceEnabled'
  | 'previsitEthnicityOptions'
  | 'previsitMaritalStatusOptions'
  | 'previsitMedicationComplianceOptions'
  | 'previsitMergeSubscriberGuarantorsEnabled'
  | 'previsitMultipleLanguages'
  | 'previsitMultipleRaces'
  | 'previsitPayorDisplayMapping'
  | 'previsitPreferredLanguageOptions'
  | 'previsitPreferredPronounOptions'
  | 'previsitProviderNameDisplays'
  | 'previsitSexOptions'
  | 'previsitSkipIntroSingleForm'
  | 'previsitUseNotionalSchedulingTimeSlots'
  | 'previsitWrittenLanguageOptions'
  | 'proceduresOptionsPreference'
  | 'raceOptions'
  | 'raceOptionsDisplayOverrides'
  | 'registrationAdditionalPatientMatchingInputs'
  | 'registrationDefaultAdditionalLoginInputsRequiredMatchCount'
  | 'registrationMaxLoginAttempts'
  | 'religionOptions'
  | 'reviewScreenCustomPatientGenderLabel'
  | 'scheduleAppointmentRedirectUrl'
  | 'schedulingDefaultEndLookaheadTime'
  | 'schedulingDefaultStartLookaheadTime'
  | 'genderIdentityOptions'
  | 'showArrivalTimeOnly'
  | 'skipSendingAppointmentConfirmationOnBooking'
  | 'stripeTestModeEnabled'
  | 'subscriberRelationshipsToGuarantor'
  | 'substanceNameOptions'
  | 'substanceUsageStatuses'
  | 'tobaccoStatuses'
  | 'tobaccoTreatments'
  | 'unsupportedCardBrands'
> {
  collectFeedback: boolean;
  collectSubscriberInfo: boolean;
  introLabel: string;
  lastLabel: string;
  medicalConditionsOptions: MedicalConditionListItem[];
  familyMedicalConditionsOptions: MedicalConditionListItem[];
  selfPayFlatFeeEnabled?: boolean;
}

export interface ProviderAssetSignedUrls {
  fileName: string;
  thumbnail: string;
  thumbnail3x: string;
  provider: string;
}

export interface PatientData {
  previsitFormSet: PrevisitFormSet.PrevisitFormSetWithEhrInformation;
  patient: Patient.PatientForPatientData;
  practice: Practice.Practice;
  encounter?: Encounter.Encounter;
  provider?: User.User;
}

export interface FindAllOptions {
  q?: string;
  deleted?: 'all' | 'true' | 'false';
  organizationId?: string;
  labels?: string[];
}

export enum ReconciliationStatus {
  Success = 'SUCCESS',
  Fail = 'FAIL',
}

// helper methods
export function castToBoolean(value: string): boolean | undefined {
  if (value === 'true') {
    return true;
  } else if (value === 'false') {
    return false;
  }
}

export function dateToString(date: Date | undefined) {
  if (!date || isNaN(date.valueOf())) {
    return '';
  } else {
    return `${date.getUTCMonth() + 1}/${date.getUTCDate()}/${date.getUTCFullYear()}`;
  }
}

export function stringToDate(value: string): Date | undefined {
  const parts = value.split('/');
  if (parts.length !== 3) {
    return;
  }

  const [month, day, year] = parts;
  const date = new Date(0);
  date.setUTCMonth(+month - 1);
  date.setUTCDate(+day);
  date.setUTCFullYear(+year);
  return date;
}
